import React, { useMemo, useState, useEffect } from "react";
import { X, ChevronRight, ChevronLeft } from "lucide-react";

/**
 * EmployeeDistributionChart — department headcount as a ranked bar list.
 *
 * Department sizes are a MAGNITUDE comparison of a single series, so the form
 * is a one-hue bar list (length carries the value; color adds nothing). Each
 * row sets the name and count on one line with a slim full-width bar beneath:
 * names get the whole row instead of truncating in a narrow label column,
 * every bar shares one baseline, and counts align at the end. "Unassigned"
 * and the folded "Other" tail are de-emphasized in gray so the organized
 * departments stay the subject.
 */
const BRAND = { light: "#2a78d6", dark: "#4a9be0" };
const GRAY = { light: "#b6c2d1", dark: "rgba(255,255,255,0.24)" };

// Top departments shown before the tail folds into one "Other" row — keeps the
// card close to the height of the attendance card beside it.
const MAX_ROWS = 6;

const EmployeeDistributionChart = ({
  data = [], // [{ name, count }] sorted desc
  theme = "light",
  language = "en",
}) => {
  const dark = theme === "dark";
  const isRTL = language === "ar";
  const [hover, setHover] = useState(null);
  const [animated, setAnimated] = useState(false);
  const [otherOpen, setOtherOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setAnimated(true), 60);
    return () => clearTimeout(t);
  }, []);

  const rows = useMemo(() => {
    const clean = (data || [])
      .map((d) => ({ name: String(d.name || "").trim() || "—", count: Number(d.count || 0) }))
      .filter((d) => d.count > 0);
    const head = clean.slice(0, MAX_ROWS);
    const tail = clean.slice(MAX_ROWS);
    if (tail.length) {
      head.push({
        name: isRTL ? `أقسام أخرى (${tail.length})` : `Other departments (${tail.length})`,
        count: tail.reduce((s, d) => s + d.count, 0),
        muted: true,
        isOther: true,
        items: tail,
      });
    }
    return head.map((d) => ({
      ...d,
      muted: d.muted || /^unassigned$/i.test(d.name),
      label: /^unassigned$/i.test(d.name) && isRTL ? "غير محدد" : d.name,
    }));
  }, [data, isRTL]);

  const maxCount = Math.max(1, ...rows.map((d) => d.count));
  const otherRow = rows.find((d) => d.isOther);

  const brand = dark ? BRAND.dark : BRAND.light;
  const gray = dark ? GRAY.dark : GRAY.light;
  const track = dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.05)";
  const rowHover = dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.035)";
  const textPrimary = dark ? "#f1f5f9" : "#1e293b";
  const textSecondary = dark ? "rgba(255,255,255,0.55)" : "#64748b";
  const Chevron = isRTL ? ChevronLeft : ChevronRight;

  return (
    <div style={{ minWidth: 0, direction: isRTL ? "rtl" : "ltr" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 2, paddingTop: 10 }}>
        {rows.map((d, i) => {
          // Floor at 2% so a 1-person department still shows a visible sliver.
          const scaled = Math.max((d.count / maxCount) * 100, 2);
          const active = hover === i;
          const dim = hover !== null && !active;
          const openOther = d.isOther ? () => setOtherOpen(true) : undefined;
          return (
            <div
              key={d.label}
              role={d.isOther ? "button" : undefined}
              tabIndex={d.isOther ? 0 : undefined}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              onFocus={() => setHover(i)}
              onBlur={() => setHover(null)}
              onClick={openOther}
              onKeyDown={
                d.isOther
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setOtherOpen(true);
                      }
                    }
                  : undefined
              }
              aria-label={d.isOther ? (isRTL ? `${d.label} — عرض التفاصيل` : `${d.label} — view details`) : undefined}
              style={{
                padding: "7px 10px",
                margin: "0 -10px",
                borderRadius: 10,
                background: active ? rowHover : "transparent",
                opacity: dim ? 0.55 : 1,
                transition: "background .15s ease, opacity .15s ease",
                cursor: d.isOther ? "pointer" : "default",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  marginBottom: 7,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0 }}>
                  <span
                    title={d.label}
                    style={{
                      fontSize: 13,
                      fontWeight: d.muted ? 500 : 600,
                      lineHeight: 1.35,
                      color: d.muted ? textSecondary : textPrimary,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {d.label}
                  </span>
                  {d.isOther && (
                    <Chevron
                      size={15}
                      color={textSecondary}
                      style={{
                        flexShrink: 0,
                        transform: active ? `translateX(${isRTL ? -2 : 2}px)` : "none",
                        transition: "transform .15s ease",
                      }}
                    />
                  )}
                </div>
                <span
                  style={{
                    flexShrink: 0,
                    fontSize: 13,
                    fontWeight: 700,
                    lineHeight: 1.35,
                    color: d.muted ? textSecondary : textPrimary,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {d.count}
                </span>
              </div>

              {/* Track + bar. Both are square at the baseline (row start) and
                  rounded at the data end; logical radii mirror in RTL, and the
                  block-level fill starts at the inline start on its own. */}
              <div
                style={{
                  height: 8,
                  background: track,
                  borderStartEndRadius: 4,
                  borderEndEndRadius: 4,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: animated ? `${scaled}%` : "0%",
                    background: d.muted ? gray : brand,
                    borderStartEndRadius: 4,
                    borderEndEndRadius: 4,
                    filter: active && !d.muted ? "brightness(1.1)" : "none",
                    transition: `width .7s cubic-bezier(.4,0,.2,1) ${i * 45}ms, filter .15s ease`,
                  }}
                />
              </div>
            </div>
          );
        })}

        {rows.length === 0 && (
          <div style={{ padding: "24px 0", fontSize: 13, color: textSecondary, textAlign: "center" }}>
            {isRTL ? "لا توجد بيانات أقسام" : "No department data"}
          </div>
        )}
      </div>

      {otherOpen && otherRow && (
        <div
          onClick={() => setOtherOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            backdropFilter: "blur(2px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: dark ? "#1e293b" : "#fff",
              borderRadius: 14,
              padding: 20,
              width: "min(420px, 90vw)",
              maxHeight: "70vh",
              overflowY: "auto",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              WebkitOverflowScrolling: "touch",
              boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
              direction: isRTL ? "rtl" : "ltr",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: textPrimary }}>
                {otherRow.label}
              </h3>
              <button
                onClick={() => setOtherOpen(false)}
                aria-label={isRTL ? "إغلاق" : "Close"}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: textSecondary,
                  padding: 4,
                  display: "flex",
                }}
              >
                <X size={18} />
              </button>
            </div>

            <p style={{ margin: "0 0 12px", fontSize: 12.5, color: textSecondary }}>
              {isRTL
                ? `دي الأقسام الأصغر (${otherRow.items.length}) اللي بعد أكبر ${MAX_ROWS} أقسام، بنجمعها في صف واحد عشان القائمة تفضل مختصرة.`
                : `The ${otherRow.items.length} smallest departments (beyond the top ${MAX_ROWS}) are grouped into this row to keep the list compact.`}
            </p>

            <div>
              {otherRow.items
                .slice()
                .sort((a, b) => b.count - a.count)
                .map((item) => (
                  <div
                    key={item.name}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      padding: "8px 0",
                      borderBottom: dark
                        ? "1px solid rgba(255,255,255,0.06)"
                        : "1px solid rgba(15,23,42,0.06)",
                      fontSize: 13,
                    }}
                  >
                    <span style={{ color: textPrimary }}>{item.name}</span>
                    <span
                      style={{
                        color: textPrimary,
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {item.count}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeDistributionChart;
